import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { codeToHtml } from 'shiki';
import { cn } from '@/lib/utils';

interface Props {
  code: string;
  language?: string;
  /** when total lines exceed this, the block collapses by default */
  maxLines?: number;
  /** force-collapse override (useful for inline contexts) */
  collapsedByDefault?: boolean;
}

const cache = new Map<string, string>();
const DEFAULT_MAX_LINES = 12;
const COLLAPSED_HEIGHT_PX = 200; // ~12 lines at 12.5/1.55

export function CollapsibleCode({
  code,
  language = 'text',
  maxLines = DEFAULT_MAX_LINES,
  collapsedByDefault,
}: Props) {
  const lineCount = useMemo(() => code.split('\n').length, [code]);
  const overflows = lineCount > maxLines || code.length > 800;
  const [collapsed, setCollapsed] = useState(collapsedByDefault ?? overflows);
  const [copied, setCopied] = useState(false);

  const [html, setHtml] = useState<string | null>(
    () => cache.get(`${language}::${code}`) ?? null
  );

  useEffect(() => {
    const isDark = typeof document !== 'undefined' &&
      document.documentElement.dataset.theme === 'dark';
    const theme = isDark ? 'github-dark' : 'github-light';
    const key = `${theme}::${language}::${code}`;
    const cached = cache.get(key);
    if (cached) {
      setHtml(cached);
      return;
    }
    let cancelled = false;
    codeToHtml(code, {
      lang: language,
      theme,
      transformers: [
        {
          pre(node) {
            if (typeof node.properties?.style === 'string') {
              node.properties.style = (node.properties.style as string)
                .replace(/background-color:[^;]+;?/g, '')
                .replace(/color:[^;]+;?/g, '');
            }
          },
        },
      ],
    })
      .then((out) => { if (!cancelled) { cache.set(key, out); setHtml(out); } })
      .catch(() => { if (!cancelled) setHtml(null); });
    return () => { cancelled = true; };
  }, [code, language]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* noop */ }
  };

  return (
    <div className="my-2 overflow-hidden rounded-md border border-border bg-surface">
      {/* header */}
      <div className="flex items-center gap-2 border-b border-border bg-surface px-2.5 py-1.5">
        <span className="font-mono text-2xs uppercase tracking-wide text-fg-subtle">
          {language === 'text' ? 'code' : language}
        </span>
        <span className="text-2xs text-fg-subtle">·</span>
        <span className="text-2xs text-fg-subtle tabular-nums">{lineCount} {lineCount === 1 ? 'line' : 'lines'}</span>
        <button
          type="button"
          onClick={onCopy}
          className="focus-ring ml-auto rounded p-0.5 text-fg-subtle hover:text-fg"
          aria-label="Copy code"
          title="Copy"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </button>
        {overflows && (
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="focus-ring inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-2xs font-medium text-fg-muted hover:bg-surface-2 hover:text-fg"
          >
            {collapsed ? (
              <>
                <ChevronDown className="h-3 w-3" />
                Expand
              </>
            ) : (
              <>
                <ChevronUp className="h-3 w-3" />
                Collapse
              </>
            )}
          </button>
        )}
      </div>

      {/* body */}
      <div
        className={cn(
          'relative overflow-hidden transition-[max-height] duration-200',
        )}
        style={{
          maxHeight: collapsed ? `${COLLAPSED_HEIGHT_PX}px` : 'none',
        }}
      >
        <div className="prose-xoro [&>pre]:!my-0 [&>pre]:!rounded-none [&>pre]:!border-0">
          {html ? (
            <div dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <pre><code>{code}</code></pre>
          )}
        </div>
        {collapsed && overflows && (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="focus-ring absolute inset-x-0 bottom-0 flex h-12 items-end justify-center bg-gradient-to-t from-surface to-transparent pb-1.5 text-2xs font-medium text-fg-muted hover:text-fg"
          >
            Show {lineCount - Math.round(maxLines * 0.6)} more lines
          </button>
        )}
      </div>
    </div>
  );
}
